import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';

const TIPOS_FAMILIA = [
  { value: '0', label: '0 - Moeda a Vista' },
  { value: '1', label: '1 - Moeda a Receber' },
  { value: '2', label: '2 - Difícil Liquidação' },
  { value: '3', label: '3 - Estoque' },
  { value: '4', label: '4 - Investimentos' },
  { value: '5', label: '5 - Imobilizado' },
  { value: '6', label: '6 - Moeda a Pagar' },
  { value: '7', label: '7 - Patrimônio Líquido' },
];

const STATUS_FAMILIA = [
  { value: 'A', label: 'Ativa' },
  { value: 'I', label: 'Inativa' },
];

const validationSchema = Yup.object().shape({
  nome: Yup.string()
    .required('Descrição é obrigatória')
    .max(40, 'Descrição deve ter no máximo 40 caracteres'),
  tipo: Yup.string().required('Tipo é obrigatório'),
  taxa_dep: Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .min(0)
    .when('tipo', {
      is: '3',
      then: Yup.number()
        .nullable()
        .transform((value, original) => (original === '' || original === null ? null : value))
        .required('Taxa Adm. obrigatória para Estoque')
        .moreThan(0, 'Taxa Adm. inválida!'),
    }),
  preco_base_padrao: Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .min(0),
  status: Yup.string().required('Status é obrigatório'),
  dias: Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .min(0, 'Dias vencimento deve ser maior ou igual a zero'),
  codigo_grupo_familia: Yup.number().nullable(),
  conta_depre: Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value)),
  cc_depre: Yup.number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value)),
});

const INITIAL_VALUES = {
  nome: '',
  tipo: '',
  taxa_dep: '',
  preco_base_padrao: '',
  status: 'A',
  dias: '',
  codigo_grupo_familia: null,
  conta_depre: '',
  cc_depre: '',
};

const ModalFamiliaAtivo = ({ show, onHide, familia, gruposFamilia, onSalvar }) => {
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    if (show && familia) {
      carregarDetalhe(familia.codigo);
    }
    if (!show) {
      setDetalhe(null);
    }
  }, [show, familia]);

  const carregarDetalhe = async (codigo) => {
    setLoadingDetalhe(true);
    try {
      const res = await api.buscarFamiliaAtivo(codigo);
      setDetalhe(res);
    } catch (e) {
      toast.error('Erro ao carregar dados da família');
      onHide();
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const getInitialValues = () => {
    if (familia && detalhe) {
      return {
        nome: detalhe.nome || '',
        tipo: detalhe.tipo || '',
        taxa_dep: detalhe.taxa_dep != null ? detalhe.taxa_dep : '',
        preco_base_padrao: detalhe.preco_base_padrao != null ? detalhe.preco_base_padrao : '',
        status: detalhe.status || 'A',
        dias: detalhe.dias != null ? detalhe.dias : '',
        codigo_grupo_familia: detalhe.codigo_grupo_familia != null ? detalhe.codigo_grupo_familia : null,
        conta_depre: detalhe.conta_depre != null ? detalhe.conta_depre : '',
        cc_depre: detalhe.cc_depre != null ? detalhe.cc_depre : '',
      };
    }
    return INITIAL_VALUES;
  };

  const gruposOptions = (gruposFamilia || []).map((g) => ({
    value: g.codigo,
    label: `${g.codigo} - ${g.nome}`,
  }));

  const handleSubmit = async (values, formikHelpers) => {
    const dados = {
      nome: values.nome,
      tipo: values.tipo,
      taxa_dep: values.taxa_dep !== '' && values.taxa_dep !== null ? parseFloat(values.taxa_dep) : null,
      preco_base_padrao: values.preco_base_padrao !== '' && values.preco_base_padrao !== null ? parseFloat(values.preco_base_padrao) : null,
      status: values.status,
      dias: values.dias !== '' && values.dias !== null ? parseInt(values.dias, 10) : null,
      codigo_grupo_familia: values.codigo_grupo_familia != null ? values.codigo_grupo_familia : null,
      conta_depre: values.conta_depre !== '' && values.conta_depre !== null ? parseInt(values.conta_depre, 10) : null,
      cc_depre: values.cc_depre !== '' && values.cc_depre !== null ? parseInt(values.cc_depre, 10) : null,
    };
    try {
      if (familia) {
        await api.atualizarFamiliaAtivo(familia.codigo, dados);
        toast.success('Família atualizada com sucesso!');
      } else {
        await api.incluirFamiliaAtivo(dados);
        toast.success('Família criada com sucesso!');
      }
      onSalvar();
    } catch (err) {
      const mensagem = err && err.message
        ? err.message
        : 'Erro ao salvar família';
      toast.error(mensagem);
    }
    formikHelpers.setSubmitting(false);
  };

  const edicao = !!familia;
  const pronto = !edicao || (!loadingDetalhe && !!detalhe);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ color: '#fff' }}>
          {edicao ? 'Editar Família de Ativo' : 'Nova Família de Ativo'}
        </Modal.Title>
      </Modal.Header>

      {!pronto ? (
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="mr-2" />
          Carregando...
        </Modal.Body>
      ) : (
        <Formik
          key={familia ? familia.codigo : 'novo'}
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => {
            const tipoSelecionado = TIPOS_FAMILIA.find((t) => t.value === formik.values.tipo) || null;
            const statusSelecionado = STATUS_FAMILIA.find((s) => s.value === formik.values.status) || null;
            const grupoSelecionado = formik.values.codigo_grupo_familia != null
              ? gruposOptions.find((g) => g.value === formik.values.codigo_grupo_familia) || null
              : null;

            return (
              <Form onSubmit={formik.handleSubmit}>
                <Modal.Body>
                  <Row>
                    {edicao && (
                      <Col xs={12} md={3}>
                        <Form.Group>
                          <Form.Label>Código</Form.Label>
                          <Form.Control
                            type="text"
                            value={familia.codigo}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                    )}
                    <Col xs={12} md={edicao ? 9 : 12}>
                      <Form.Group>
                        <Form.Label>Descrição <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="nome"
                          maxLength={40}
                          value={formik.values.nome}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.nome && !!formik.errors.nome}
                          placeholder="Descrição da família (máx. 40 caracteres)"
                          autoFocus={!edicao}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.nome}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">{(formik.values.nome || '').length}/40</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Tipo <span className="text-danger">*</span></Form.Label>
                        <Select
                          options={TIPOS_FAMILIA}
                          value={tipoSelecionado}
                          onChange={(op) => formik.setFieldValue('tipo', op ? op.value : '')}
                          onBlur={() => formik.setFieldTouched('tipo', true)}
                          placeholder="Selecione o tipo..."
                          isClearable={false}
                          classNamePrefix="react-select"
                        />
                        {formik.touched.tipo && formik.errors.tipo && (
                          <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>
                            {formik.errors.tipo}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Grupo de Família</Form.Label>
                        <Select
                          options={gruposOptions}
                          value={grupoSelecionado}
                          onChange={(op) => formik.setFieldValue('codigo_grupo_familia', op ? op.value : null)}
                          placeholder="Selecione o grupo..."
                          isClearable
                          classNamePrefix="react-select"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                        <Select
                          options={STATUS_FAMILIA}
                          value={statusSelecionado}
                          onChange={(op) => formik.setFieldValue('status', op ? op.value : '')}
                          isClearable={false}
                          classNamePrefix="react-select"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label>
                          Deprec. (Mês)
                          {formik.values.tipo === '3' && <span className="text-danger"> *</span>}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="taxa_dep"
                          step="0.0001"
                          min="0"
                          value={formik.values.taxa_dep}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.taxa_dep && !!formik.errors.taxa_dep}
                          placeholder="0.0000"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.taxa_dep}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label>Preço Base Padrão</Form.Label>
                        <Form.Control
                          type="number"
                          name="preco_base_padrao"
                          step="0.0001"
                          min="0"
                          value={formik.values.preco_base_padrao}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.preco_base_padrao && !!formik.errors.preco_base_padrao}
                          placeholder="0.0000"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.preco_base_padrao}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label>Dias Vencimento</Form.Label>
                        <Form.Control
                          type="number"
                          name="dias"
                          min="0"
                          value={formik.values.dias}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.dias && !!formik.errors.dias}
                          placeholder="0"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.dias}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label>Conta Desp. Depreciação</Form.Label>
                        <Form.Control
                          type="number"
                          name="conta_depre"
                          value={formik.values.conta_depre}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.conta_depre && !!formik.errors.conta_depre}
                          placeholder="Código da conta"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.conta_depre}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label>CC Desp. Depreciação</Form.Label>
                        <Form.Control
                          type="number"
                          name="cc_depre"
                          value={formik.values.cc_depre}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.cc_depre && !!formik.errors.cc_depre}
                          placeholder="Código CC"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.cc_depre}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={onHide} disabled={formik.isSubmitting}>
                    Cancelar
                  </Button>
                  <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
                    {formik.isSubmitting ? (
                      <><Spinner animation="border" size="sm" className="mr-1" />Salvando...</>
                    ) : 'Salvar'}
                  </Button>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

export default ModalFamiliaAtivo;
